import React, { FC, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import FilterCheckbox from '@buyside/components/presentation/Filters/FilterCheckbox/filterCheckbox';
import FundProfileIndexItem from '@buyside/components/presentation/FundProfileIndexPage/FundProfileIndexItem/fundProfileIndexItem';
import FundProfileCardRecommendationContainer from '@buyside/components/container/FundProfileIndex/FundProfileCardRecommendationContainer';
import FundProfileFilters, {
  renderFundCheckboxes,
  renderFundSortOrders
} from '@buyside/components/presentation/FundProfileIndexPage/fundProfileFilters';
import IndexPageHeader from '@buyside/components/presentation/IndexPageHeader/indexPageHeader';
import IndexPageListArea from '@buyside/components/presentation/IndexPageListArea/indexPageListArea';
import IndexPaginationNav from '@buyside/components/presentation/IndexPaginationNav/indexPaginationNav';

import { validatePageParam } from '@buyside/util/numbers';
import { BREAKPOINTS, useIsMobile } from '@buyside/util/hooks/useIsMobile';
import { inviteMembersUrl } from '@buyside/util/urlHelper';
const { indexTypes } = require('buyside/util/constantsFromRails.json');

import {
  useFundProfileIndexListQuery,
  useFundProfileCurrentUserQuery,
  useFundProfileRecommendationsQuery
} from '@interfaces/graphql-typings';

import styles from './fundProfileIndexPageStyles.scss';

const FundProfileIndexPage: FC = () => {
  const isMobile = useIsMobile(BREAKPOINTS.LARGE);

  const [searchParams] = useSearchParams();
  const page = validatePageParam(searchParams);
  const showRecommendationsOnly = searchParams.get('recommended') === 'true';

  const {
    data,
    previousData,
    loading: fundProfilesLoading,
    error: fundProfilesError
  } = useFundProfileIndexListQuery({
    variables: { indexType: indexTypes.fund, searchParams: searchParams.toString() },
    skip: showRecommendationsOnly
  });

  if (fundProfilesError) throw fundProfilesError;

  const {
    data: userData,
    previousData: prevUserData,
    loading: userLoading,
    error: fundProfileCurrentUserError
  } = useFundProfileCurrentUserQuery();

  if (fundProfileCurrentUserError) throw fundProfileCurrentUserError;

  const {
    data: recommendationsData,
    loading: recommendationsLoading,
    error: recommendationsError
  } = useFundProfileRecommendationsQuery({
    variables: { showAll: showRecommendationsOnly },
    skip:
      Boolean(
        Array.from(searchParams.keys()).filter(key => !['page', 'recommended'].includes(key)).length
      ) || !(page === 1 || showRecommendationsOnly)
  });

  if (recommendationsError) throw recommendationsError;

  const fundProfilesIndexList = (data || previousData)?.fundProfilesIndexList;
  const fundProfilesIndexListResultCount =
    (data || previousData)?.fundProfilesIndexListResultCount || 0;
  const currentUser = (userData || prevUserData)?.currentUser;
  const fundRecommendations = recommendationsData?.fundRecommendations;
  const recommendedFundsCount = fundRecommendations ? fundRecommendations?.edges?.length : 0;
  const introRequestCount = currentUser?.introRequestCount || 0;
  const showAllocatorNotification = currentUser?.isAllocator && introRequestCount > 0;

  const renderCheckboxes = useCallback(
    () => renderFundCheckboxes(Boolean(isMobile && showAllocatorNotification)),
    [isMobile, showAllocatorNotification]
  );
  const renderFilters = useCallback(() => <FundProfileFilters />, []);
  const renderSideButtons = useCallback(
    () => (
      <>
        {!isMobile && showAllocatorNotification && (
          <section className={styles.allocatorIntroRequests}>
            <div>
              <strong>{introRequestCount}</strong>
              {introRequestCount > 1 ? ' funds have' : ' fund has'} requested an introduction.
            </div>
            <FilterCheckbox text="Show Interested Funds" paramName="intro_request" />
          </section>
        )}
        <Button
          type={BUTTON_TYPES.PRIMARY}
          size={BUTTON_SIZES.SMALL}
          text="Invite New Members"
          url={inviteMembersUrl()}
        />
      </>
    ),
    [introRequestCount, isMobile, showAllocatorNotification]
  );

  return (
    <main className={styles.main}>
      <div className={styles.indexPageContainer}>
        <div className={styles.mainIndexPageArea}>
          <IndexPageHeader
            header={showRecommendationsOnly ? 'Recommended Funds' : 'Funds'}
            renderCheckboxes={renderCheckboxes}
            renderFilters={renderFilters}
            renderSideButtons={renderSideButtons}
            renderSortOrders={renderFundSortOrders}
            resultCount={
              showRecommendationsOnly && recommendedFundsCount
                ? recommendedFundsCount
                : fundProfilesIndexListResultCount
            }
            showMoreLess
          />
          <IndexPageListArea
            data={fundProfilesIndexList}
            loading={fundProfilesLoading || recommendationsLoading || userLoading}
            currentUser={currentUser}
            card={FundProfileIndexItem}
            recommendationComponent={FundProfileCardRecommendationContainer}
            recommendations={fundRecommendations}
            showOnlyRecommended={showRecommendationsOnly}
            isMobile={isMobile}
          />
          {showRecommendationsOnly ? null : (
            <IndexPaginationNav
              resultCount={fundProfilesIndexListResultCount}
              loading={fundProfilesLoading || recommendationsLoading}
              resultLength={fundProfilesIndexList ? fundProfilesIndexList.length : 0}
              resultsPerPage={20}
            />
          )}
        </div>
      </div>
    </main>
  );
};

export default FundProfileIndexPage;
